import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import { T } from 'gt-react';
import { WorkbookItemIcon } from './workbookItemIcon';

/** Whether a drag carries files (not text or a link). */
function carriesFiles(event: DragEvent<HTMLElement>): boolean {
  return Array.from(event.dataTransfer.types).includes('Files');
}

/**
 * Takes files dropped anywhere on `children`, outlining the area while files
 * are dragged over it. Without `onFiles` it is only `children`.
 */
export function FileDropArea({
  onFiles,
  children,
}: {
  onFiles: ((files: File[]) => void) | undefined;
  children: ReactNode;
}) {
  // Entering a child fires enter on it before leave on the parent: count them.
  const depth = useRef(0);
  const [dragging, setDragging] = useState(false);
  if (!onFiles) return <div className="flex h-full flex-col">{children}</div>;
  return (
    <div
      className="relative flex h-full flex-col"
      onDragEnter={(event) => {
        if (!carriesFiles(event)) return;
        event.preventDefault();
        depth.current += 1;
        setDragging(true);
      }}
      onDragOver={(event) => {
        if (!carriesFiles(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
      }}
      onDragLeave={(event) => {
        if (!carriesFiles(event)) return;
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setDragging(false);
      }}
      onDrop={(event) => {
        if (!carriesFiles(event)) return;
        event.preventDefault();
        depth.current = 0;
        setDragging(false);
        const files = Array.from(event.dataTransfer.files);
        if (files.length) onFiles(files);
      }}
    >
      {children}
      {dragging && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-background/80"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-foreground">
            <WorkbookItemIcon kind="xlsx" className="size-5" />
            <T context="workbook add data: drop files here to upload them">
              Drop an Excel or CSV file to add it
            </T>
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * The Add data panel's description: the kinds of data to choose from, and the
 * Excel file to drop, which can also be picked with the file chooser.
 */
export function AddDataDescription({
  onFiles,
}: {
  onFiles: ((files: File[]) => void) | undefined;
}) {
  const input = useRef<HTMLInputElement>(null);
  if (!onFiles)
    return (
      <p className="text-sm text-muted-foreground">
        <T context="workbook source catalog description">
          Choose a data source, a data mapping, a data asset or an entity.
        </T>
      </p>
    );
  return (
    <p className="text-sm text-muted-foreground">
      <T context="workbook source catalog description, with a file to drop">
        Choose a data source, a data mapping, a data asset or an entity, or drag
        and drop{' '}
        <button
          type="button"
          className="inline-flex items-baseline gap-1 rounded-sm text-foreground underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => input.current?.click()}
        >
          <WorkbookItemIcon
            kind="xlsx"
            className="size-3.5 self-center"
            aria-hidden
          />
          an Excel file
        </button>
        .
      </T>
      <input
        ref={input}
        type="file"
        accept=".xlsx,.csv"
        multiple
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          event.target.value = '';
          if (files.length) onFiles(files);
        }}
      />
    </p>
  );
}
